import { useTranslation } from "react-i18next";

import { FormContainer, Treturn, useFetch } from "@4uhub/lib4uhub";
import { useCallback } from "react";
import { IMessageProps } from "../models";
import {
  createTicketAutomationReceivedMessage,
  getTicketAutomationReceivedMessageById,
  updateTicketAutomationReceivedMessageById,
} from "../../../../../../../services/ticketAutomationReceivedMessage.service";
import { Grid } from "@mui/material";
import {
  automationReceivedMessagesSchema,
  TAutomationReceivedMessagesForm,
} from "./AutomationReceivedMessagesSchema";
import { DefaultFields } from "./DefaultFields";
import { ReturnAutomationFields } from "./ReturnAutomationFields";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedMessages.";

const AutomationReceivedMessages = ({
  id,
  ticketAutomationReceivedRuleId,
  automationCode,
  onBackButton,
}: IMessageProps) => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutomationReceivedMessage,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutomationReceivedMessageById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutomationReceivedMessageById,
  );

  const saveHandler = useCallback(
    (v: TAutomationReceivedMessagesForm) =>
      save({
        ...v,
        returnDays: v.returnDays ? Number(v.returnDays) : undefined,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
        showAppAndOnlineCalendar: v.showAppAndOnlineCalendar,
        ticketAutomationReceivedCaleSpecialties: v.calendarSpecialties?.map(
          (c) => ({
            externalId: c.id,
            externalName: c.name,
          }),
        ),
        ticketAutomationReceivedMessageCaleClassifications:
          v.calendarClassification?.map((c) => ({
            externalId: c.id,
            externalName: c.name,
          })),
        ticketAutomationReceivedMessageCaleGroupings:
          v.calendarConsultationGroupingRegister?.map((c) => ({
            externalId: c.id,
            externalName: c.name,
          })),
        ticketAutomationReceivedMessageCalePreferences:
          v.calendarPreferences?.map((c) => ({
            externalId: c.id,
            externalName: c.name,
          })),
        ticketAutomationReceivedMessageCalendars: v.calendars?.map((c) => ({
          externalId: c.id,
          externalName: c.name,
        })),
      }),
    [save],
  );

  const updateHandler = useCallback(
    (v: TAutomationReceivedMessagesForm, id: string) =>
      update({
        item: {
          id: id,
          ...v,
          returnDays: v.returnDays ? Number(v.returnDays) : undefined,
          showAppAndOnlineCalendar: v.showAppAndOnlineCalendar,
          ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
          ticketAutomationReceivedCaleSpecialties: v.calendarSpecialties?.map(
            (c) => ({
              externalId: c.id,
              externalName: c.name,
            }),
          ),
          ticketAutomationReceivedMessageCaleClassifications:
            v.calendarClassification?.map((c) => ({
              externalId: c.id,
              externalName: c.name,
            })),
          ticketAutomationReceivedMessageCaleGroupings:
            v.calendarConsultationGroupingRegister?.map((c) => ({
              externalId: c.id,
              externalName: c.name,
            })),
          ticketAutomationReceivedMessageCalePreferences:
            v.calendarPreferences?.map((c) => ({
              externalId: c.id,
              externalName: c.name,
            })),
          ticketAutomationReceivedMessageCalendars: v.calendars?.map((c) => ({
            externalId: c.id,
            externalName: c.name,
          })),
        },
        id: id,
      }),
    [id, update],
  );

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TAutomationReceivedMessagesForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TAutomationReceivedMessagesForm = {
          ...data,
          returnDays: data.returnDays?.toString(),
          showAppAndOnlineCalendar: data.showAppAndOnlineCalendar,
          calendarClassification:
            data.ticketAutomationReceivedMessageCaleClassifications?.map(
              (c) => ({
                id: c.externalId,
                name: c.externalName,
              }),
            ),
          calendarConsultationGroupingRegister:
            data.ticketAutomationReceivedMessageCaleGroupings?.map((c) => ({
              id: c.externalId,
              name: c.externalName,
            })),
          calendarPreferences:
            data.ticketAutomationReceivedMessageCalePreferences?.map((c) => ({
              id: c.externalId,
              name: c.externalName,
            })),
          calendarSpecialties:
            data.ticketAutomationReceivedCaleSpecialties?.map((c) => ({
              id: c.externalId,
              name: c.externalName,
            })),
          calendars: data.ticketAutomationReceivedMessageCalendars?.map(
            (c) => ({
              id: c.externalId,
              name: c.externalName,
            }),
          ),
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TAutomationReceivedMessagesForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "message")}
      schema={automationReceivedMessagesSchema}
      subtitle={t(translationPath + "new_message")}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      propId={id}
      formActionsOnBottom={false}
      defaultValues={{
        showAppAndOnlineCalendar: false,
      }}
      fields={() => (
        <Grid container spacing={2}>
          {automationCode === "25" && (
            <ReturnAutomationFields automationCode={automationCode} />
          )}
          {automationCode !== "25" && (
            <DefaultFields automationCode={automationCode} />
          )}
        </Grid>
      )}
    />
  );
};

export default AutomationReceivedMessages;
